import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { ControllerLayout } from "./modules/controller/layouts/ControllerLayout";
import { ContentDirectorLayout } from "./modules/controller/layouts/ContentDirectorLayout";
import { AdminLayout } from "./modules/admin/layouts/AdminLayout";
import { SuperadminLayout } from "./modules/admin/layouts/SuperadminLayout";
import { LoggerLayout } from "./modules/logger/layouts/LoggerLayout";
import { Discovery } from "./modules/discovery/views/Discovery";
import { StoryProducer } from "./modules/storyproducer/views/StoryProducer";
import { ShowRunnerLayout } from "./modules/showrunner/views/ShowRunnerLayout";
import { ExecutiveLayout } from "./modules/executive/layouts/ExecutiveLayout";
import { EditorLayout } from "./modules/editor/layouts/EditorLayout";
import { ClienteLayout } from "./modules/cliente/layouts/ClienteLayout";
import { RoadmapView } from "./modules/roadmap/views/RoadmapView";
import { ProjectSelector } from "./modules/auth/views/ProjectSelector";
import { ModuleSelector } from "./modules/auth/views/ModuleSelector";
import { UpdatePassword } from "./modules/auth/views/UpdatePassword";
import { TagsProvider } from "./modules/common/contexts/TagsContext";
import { AuthProvider, useAuth } from "./modules/auth/contexts/AuthContext";
import { SelectionProvider } from "./modules/common/contexts/SelectionContext";
import { ChatProvider } from "./modules/common/contexts/ChatContext";
import { ProjectProvider, useProjects } from "./modules/common/contexts/ProjectContext";
import { LoadingScreen } from "./modules/common/components/LoadingScreen";
import { ProjectSlugGuard } from "./modules/common/components/ProjectSlugGuard";

const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  allowedRoles: string[];
}> = ({ children, allowedRoles }) => {
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { isLoading: isProjectLoading } = useProjects();
  const { projectSlug } = useParams();

  if (isAuthLoading || isProjectLoading) {
    return <LoadingScreen />;
  }

  // Redirect to login if not authenticated
  const fallback = projectSlug ? `/${projectSlug}` : "/login";
  if (!isAuthenticated) return <Navigate to={fallback} replace />;
  if (user && !allowedRoles.includes(user.role)) return <Navigate to={fallback} replace />;

  return <>{children}</>;
};

import { ColorLegendHelper } from "./modules/common/components/ColorLegendHelper";

function AppContent() {
  const [, setAppVersion] = useState<string>("Unknown");
  const { isLoading: isProjectLoading } = useProjects();
  const { isLoading: isAuthLoading } = useAuth();

  useEffect(() => {
    if (window.electronAPI) {
      window.electronAPI.getAppVersion().then(setAppVersion);
    }
  }, []);

  if (isProjectLoading || isAuthLoading) {
    return <LoadingScreen />;
  }

  return (
    <>
      <Routes>
        {/* Public routes */}
        <Route path="/roadmap" element={<RoadmapView />} />
        <Route path="/update-password" element={<UpdatePassword />} />

        {/* Project selector and Login (shares same component logic) */}
        <Route path="/projects" element={<ProjectSelector />} />
        <Route path="/login" element={<ProjectSelector />} />

        {/* Redirects */}
        <Route path="/" element={<Navigate to="/login" replace />} />

        {/* Project-scoped routes — nested under /:projectSlug */}
        <Route path="/:projectSlug" element={<ProjectSlugGuard />}>
          {/* Index: Module selector (login form if not auth, module cards if auth) */}
          <Route index element={<ModuleSelector />} />

          <Route
            path="logger"
            element={
              <ProtectedRoute
                allowedRoles={[
                  "Logger",
                  "Editor",
                  "Story Producer",
                  "Showrunner",
                  "Content Controller",
                  "Admin",
                  "Superadmin",
                ]}
              >
                <LoggerLayout />
              </ProtectedRoute>
            }
          />
          <Route
            path="controller"
            element={
              <ProtectedRoute allowedRoles={["Content Controller", "Admin", "Superadmin"]}>
                <ControllerLayout />
              </ProtectedRoute>
            }
          />
          <Route
            path="contentdirector"
            element={
              <ProtectedRoute allowedRoles={["Content Director", "Admin", "Superadmin"]}>
                <ContentDirectorLayout />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin"
            element={
              <ProtectedRoute allowedRoles={["Admin", "Superadmin"]}>
                <AdminLayout />
              </ProtectedRoute>
            }
          />
          <Route
            path="superadmin"
            element={
              <ProtectedRoute allowedRoles={["Superadmin"]}>
                <SuperadminLayout />
              </ProtectedRoute>
            }
          />
          <Route
            path="discovery"
            element={
              <ProtectedRoute
                allowedRoles={[
                  "Story Producer",
                  "Content Director",
                  "Showrunner",
                  "Content Controller",
                  "Admin",
                  "Superadmin",
                ]}
              >
                <Discovery />
              </ProtectedRoute>
            }
          />
          <Route
            path="storyproducer"
            element={
              <ProtectedRoute
                allowedRoles={[
                  "Editor",
                  "Story Producer",
                  "Content Director",
                  "Showrunner",
                  "Content Controller",
                  "Admin",
                  "Superadmin",
                ]}
              >
                <StoryProducer />
              </ProtectedRoute>
            }
          />
          <Route
            path="showrunner"
            element={
              <ProtectedRoute
                allowedRoles={["Showrunner", "Content Controller", "Admin", "Superadmin"]}
              >
                <ShowRunnerLayout />
              </ProtectedRoute>
            }
          />
          <Route
            path="executive"
            element={
              <ProtectedRoute allowedRoles={["Executive", "Admin", "Superadmin"]}>
                <ExecutiveLayout />
              </ProtectedRoute>
            }
          />
          <Route
            path="editor"
            element={
              <ProtectedRoute allowedRoles={["Editor", "Admin", "Superadmin"]}>
                <EditorLayout />
              </ProtectedRoute>
            }
          />
          <Route
            path="cliente"
            element={
              <ProtectedRoute allowedRoles={["Cliente", "Admin", "Superadmin"]}>
                <ClienteLayout />
              </ProtectedRoute>
            }
          />
        </Route>

        <Route path="*" element={<Navigate to="/projects" replace />} />
      </Routes>

      {/* Helper flotante para leyenda de colores */}
      <ColorLegendHelper />
    </>
  );
}

import { Toaster } from "@/components/ui/sonner";

function App() {
  return (
    <AuthProvider>
      <ProjectProvider>
        <TagsProvider>
          <ChatProvider>
            <SelectionProvider>
              <BrowserRouter>
                <AppContent />
              </BrowserRouter>
              <Toaster theme="dark" />
            </SelectionProvider>
          </ChatProvider>
        </TagsProvider>
      </ProjectProvider>
    </AuthProvider>
  );
}

export default App;
